import React from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { Carousel } from "@mantine/carousel";
import { GraduationCap, MapPin, Smile } from "lucide-react";
import "@mantine/carousel/styles.css";

interface RoomiesProps {
  propertyData: PropertyToEdit | null;
}

const Roomies: React.FC<RoomiesProps> = ({ propertyData }) => {
  const hasRoommates = propertyData?.roomies && propertyData.roomies.length > 0;
  const roommateCount = propertyData?.roomies?.length || 0;

  function getUpcomingSemester(): string {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-based
  const year = now.getFullYear();

  if (month >= 8) {
    // Aug–Dec: next semester is March–July of next year
    return `March–July ${year + 1}`;
  } else {
    // Jan–Jul: current semester is March–July, upcoming is Aug–Dec this year
    return `August–December ${year}`;
  }
}

  return (
    <div className="listingSection__wrap w-full">
      {/* Header */}
    <div className="flex items-center gap-3">
  <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
    <GraduationCap size={24} className="text-primary-600 dark:text-primary-400" />
  </div>
  <div>
    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
      Meet your roommates
    </h2>
    {hasRoommates && (
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        {roommateCount} {roommateCount === 1 ? "person" : "people"} living here
      </p>
    )}
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
      Upcoming semester: {getUpcomingSemester()}
    </p>
  </div>
</div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

      {hasRoommates ? (
        <>
          <Carousel
            withIndicators
            height={220}
            slideSize={
              roommateCount === 1
                ? "100%"
                : roommateCount === 2
                ? "50%"
                : "33.333333%"
            }
            slideGap="lg"
            loop={roommateCount > 1}
            align="start"
            slidesToScroll={1}
            styles={{
              indicator: {
                width: 8,
                height: 8,
                transition: "width 250ms ease",
                backgroundColor: "#D1D5DB",
                "&[data-active]": { width: 24, backgroundColor: "#6366F1" },
              },
              controls: { top: "50%", transform: "translateY(-50%)" },
              control: {
                backgroundColor: "white",
                border: "1px solid #E5E7EB",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                opacity: 1,
                "&:hover": { backgroundColor: "#F9FAFB" },
                "&[data-inactive]": { opacity: 0, cursor: "default" },
              },
            }}
          >
            {propertyData.roomies.map((roomie, index) => {
              const firstName = roomie.name.split(" ")[0];
              const gender = roomie.gender?.toLowerCase();
              const isFemale = gender === "female";
              const isMale = gender === "male";

              return (
                <Carousel.Slide key={roomie.id}>
                  <div className="group h-full px-1">
                    <div className="relative h-full p-4 bg-gradient-to-br from-white to-gray-50 dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                      
                      {/* Decorative corner */}
                      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-primary-100/50 to-purple-100/50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-bl-full" />

                      {/* Gender stripe accent on left edge */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${
                        isFemale ? "bg-pink-400" : isMale ? "bg-blue-400" : "bg-gray-200"
                      }`} />

                      <div className="relative flex flex-col items-center text-center gap-2">
                        {/* Avatar */}
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-purple-400 rounded-full blur-sm opacity-40 group-hover:opacity-60 transition-opacity" />
                          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/30 dark:to-orange-900/30 flex items-center justify-center border-4 border-white dark:border-neutral-700 shadow-lg">
                            <Smile size={36} className="text-primary-600 dark:text-primary-400" />
                          </div>
                          {index === 0 && (
                            <div className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-green-400 border-2 border-white dark:border-neutral-700 rounded-full" />
                          )}
                        </div>

                        {/* Name */}
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                          {firstName}
                        </h3>

                        {/* Country */}
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <MapPin size={12} />
                          <span className="text-xs">{roomie.country}</span>
                        </div>

                        {/* Gender pill — inline under country */}
                        {(isFemale || isMale) && (
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${
                            isFemale
                              ? "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800"
                              : "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
                          }`}>
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2a5 5 0 100 10A5 5 0 0012 2zm0 12c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z" />
                            </svg>
                            {isFemale ? "Female" : "Male"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Carousel.Slide>
              );
            })}
          </Carousel>

          {/* Additional Info */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <span className="font-semibold">International Community:</span> Live and connect with students from around the world. Our houses and apartments offer a truly international atmosphere that makes your student experience richer, more open, and unforgettable.
            </p>
          </div>
        </>
      ) : (
        <div className="py-12 px-6 bg-gradient-to-br from-gray-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border-2 border-dashed border-gray-300 dark:border-neutral-700">
          <div className="flex flex-col items-center text-center max-w-md mx-auto space-y-3">
            <div className="p-4 bg-gray-100 dark:bg-neutral-700 rounded-full">
              <GraduationCap size={32} className="text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              No roommates yet
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This property doesn't have any confirmed roommates at the moment.
              You could be one of the first!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roomies;